import { z } from 'zod';

import {
  BaseFlowBlockSchema,
  BlockType,
  ProducedType,
  returnTypeSchema,
} from '../base/baseSchema.js';

export enum Mode {
  DocumentTemplate = 'DocumentTemplate',
  Markdown = 'Markdown',
  Base64ToFile = 'Base64ToFile',
  Html = 'Html',
}

export enum DataSourceApiKey {
  Crossmark = 'crossmark',
  DateSigned = 'dateSigned',
  EhrScheduled = 'ehrScheduled',
  EmergencyContactName = 'emergencyContactName',
  EmergencyContactPhone = 'emergencyContactPhone',
  EmergencyContactRelationship = 'emergencyContactRelationship',
  FreehandBodyDiagram = 'freehandBodyDiagram',
  GuarantorAge = 'guarantorAge',
  GuarantorDob = 'guarantorDob',
  GuarantorHomeAddress = 'guarantorHomeAddress',
  GuarantorHomeCityStateZip = 'guarantorHomeCityStateZip',
  GuarantorHomeFullAddress = 'guarantorHomeFullAddress',
  GuarantorHomePhoneNumber = 'guarantorHomePhoneNumber',
  GuarantorName = 'guarantorName',
  GuarantorRelationship = 'guarantorRelationship', // deprecated use guarantorRelationshipToPatient
  GuarantorRelationshipToPatient = 'guarantorRelationshipToPatient',
  GuarantorSex = 'guarantorSex',
  GuarantorSsn = 'guarantorSsn',
  GuardianRelationship = 'guardianRelationship',
  Mrn = 'mrn', // deprecated use patientMrn
  NewbornAge = 'newbornAge',
  None = 'none',
  PatientAge = 'patientAge',
  PatientAthenaIdxMrn = 'patientAthenaIdxMrn',
  PatientBirthGestationalAge = 'patientBirthGestationalAge',
  PatientBirthWeightGrams = 'patientBirthWeightGrams',
  PatientCareProviderAddress = 'patientCareProviderAddress',
  PatientCareProviderName = 'patientCareProviderName',
  PatientDob = 'patientDob',
  PatientEmail = 'patientEmail',
  PatientEmployer = 'patientEmployer',
  PatientHomeAddress = 'patientHomeAddress',
  PatientHomeCityStateZip = 'patientHomeCityStateZip',
  PatientHomeFullAddress = 'patientHomeFullAddress',
  PatientHomePhoneNumber = 'patientHomePhoneNumber',
  PatientInitials = 'patientInitials',
  PatientIp = 'patientIp',
  PatientMobilePhoneNumber = 'patientMobilePhoneNumber',
  PatientMotherName = 'patientMotherName',
  PatientMrn = 'patientMrn',
  PatientName = 'patientName',
  PatientPharmacyNameWithAddress = 'patientPharmacyNameWithAddress',
  PatientPharmacyPhone = 'patientPharmacyPhone',
  PatientPreferredLanguage = 'patientPreferredLanguage',
  PatientRace = 'patientRace',
  PatientSex = 'patientSex',
  PatientSsn = 'patientSsn',
  PrimaryInsuranceGroupNumber = 'primaryInsuranceGroupNumber',
  PrimaryInsuranceInsuranceName = 'primaryInsuranceInsuranceName',
  PrimaryInsurancePolicyHolderAge = 'primaryInsurancePolicyHolderAge',
  PrimaryInsurancePolicyHolderDOB = 'primaryInsurancePolicyHolderDob',
  PrimaryInsurancePolicyHolderHomeAddress = 'primaryInsurancePolicyHolderHomeAddress',
  PrimaryInsurancePolicyHolderHomeCityStateZip = 'primaryInsurancePolicyHolderHomeCityStateZip',
  PrimaryInsurancePolicyHolderHomePhoneNumber = 'primaryInsurancePolicyHolderHomePhoneNumber',
  PrimaryInsurancePolicyHolderName = 'primaryInsurancePolicyHolderName',
  PrimaryInsurancePolicyHolderRelationshipToPatient = 'primaryInsurancePolicyHolderRelationshipToPatient',
  PrimaryInsurancePolicyHolderSex = 'primaryInsurancePolicyHolderSex',
  PrimaryInsurancePolicyNumber = 'primaryInsurancePolicyNumber',
  SecondaryInsuranceGroupNumber = 'secondaryInsuranceGroupNumber',
  SecondaryInsuranceInsuranceName = 'secondaryInsuranceInsuranceName',
  SecondaryInsurancePolicyHolderAge = 'secondaryInsurancePolicyHolderAge',
  SecondaryInsurancePolicyHolderDOB = 'secondaryInsurancePolicyHolderDob',
  SecondaryInsurancePolicyHolderHomeAddress = 'secondaryInsurancePolicyHolderHomeAddress',
  SecondaryInsurancePolicyHolderHomeCityStateZip = 'secondaryInsurancePolicyHolderHomeCityStateZip',
  SecondaryInsurancePolicyHolderHomePhoneNumber = 'secondaryInsurancePolicyHolderHomePhoneNumber',
  SecondaryInsurancePolicyHolderName = 'secondaryInsurancePolicyHolderName',
  SecondaryInsurancePolicyHolderRelationshipToPatient = 'secondaryInsurancePolicyHolderRelationshipToPatient',
  SecondaryInsurancePolicyHolderSex = 'secondaryInsurancePolicyHolderSex',
  SecondaryInsurancePolicyNumber = 'secondaryInsurancePolicyNumber',
  Signature = 'signature',
  VisitAdmitDate = 'visitAdmitDate',
  VisitAdmitDatetime = 'visitAdmitDatetime',
  VisitAdmittingPhysicianName = 'visitAdmittingPhysicianName',
  VisitAttendingPhysicianName = 'visitAttendingPhysicianName',
  VisitBuildingLocation = 'visitBuildingLocation',
  VisitEhrId = 'visitEhrId',
  VisitEhrType = 'visitEhrType',
  VisitMedicalService = 'visitMedicalService',
  VisitReason = 'visitReason',
}

export const GenerateFileBlockConfigSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal(Mode.DocumentTemplate),
    documentTemplateId: z.uuid(),
    valuesByDocumentTemplateKey: z.record(z.string(), z.string().optional()),
  }),
  z.object({
    mode: z.literal(Mode.Markdown),
    markdown: z.string(),
  }),
  z.object({
    mode: z.literal(Mode.Base64ToFile),
    base64Payload: z.string(),
  }),
  z.object({
    mode: z.literal(Mode.Html),
    html: z.string(),
  }),
]);

export type GenerateFileBlockConfig = z.infer<typeof GenerateFileBlockConfigSchema>;

export const GenerateFileBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.GenerateFile),
  config: GenerateFileBlockConfigSchema,
  returnType: returnTypeSchema(ProducedType.FileReference),
});

export type GenerateFileBlock = z.infer<typeof GenerateFileBlockSchema>;
